import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import MediaLightbox from "@/components/MediaLightbox";
import { isVideoUrl } from "@/lib/media";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";

export default function Gallery() {
  const [items, setItems] = useState([]);
  useSEO({
    title: "Galerie",
    description: "Galerie photos et vidéos des résidences et chantiers AMAR SAHRAOUI à Oran. Découvrez nos réalisations en images : architecture, intérieurs, espaces communs.",
    path: "/galerie",
  });
  const [filter, setFilter] = useState("all");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    api.get("/public/gallery").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const categories = Array.from(new Set(items.map((i) => i.category))).filter(Boolean);
  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((i) => i.category === filter)),
    [items, filter]
  );

  const mediaItems = useMemo(
    () => filtered.map((g) => ({ url: g.image_url, title: g.title })),
    [filtered]
  );

  return (
    <div>
      <section className="pt-16 pb-12 lg:pt-24 lg:pb-16 border-b atelier-border-soft bg-atelier-deep relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-20" aria-hidden="true" />
        <div className="px-5 lg:px-10 relative z-10">
          <div className="label-tech text-atelier-ink mb-4">— Galerie</div>
          <h1 className="font-serif text-5xl lg:text-7xl tracking-tight leading-none max-w-4xl text-atelier-text">
            Nos projets <span className="italic text-atelier-ink">en images.</span>
          </h1>
        </div>
      </section>

      <section className="border-b atelier-border-soft bg-atelier-deep/80 backdrop-blur-md sticky top-16 z-30">
        <div className="px-5 lg:px-10 py-4 flex gap-2 overflow-x-auto no-scrollbar">
          <button onClick={() => setFilter("all")} className={`px-3 py-2 label-tech text-[0.65rem] border transition-all ${filter === "all" ? "bg-atelier-ink text-atelier-deep border-atelier-ink" : "atelier-border-soft text-atelier-textDim hover:border-atelier-ink hover:text-atelier-ink"}`} data-testid="gallery-filter-all">Tous</button>
          {categories.map((c) => (
            <button key={c} onClick={() => setFilter(c)} className={`px-3 py-2 label-tech text-[0.65rem] border transition-all whitespace-nowrap ${filter === c ? "bg-atelier-ink text-atelier-deep border-atelier-ink" : "atelier-border-soft text-atelier-textDim hover:border-atelier-ink hover:text-atelier-ink"}`} data-testid={`gallery-filter-${c}`}>{c}</button>
          ))}
        </div>
      </section>

      <section className="py-12 lg:py-16 bg-atelier">
        <div className="px-5 lg:px-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((g, i) => {
            const isVid = isVideoUrl(g.image_url);
            return (
              <Reveal key={g.id} delay={(i % 8) * 50}>
                <button
                  onClick={() => setLightboxIndex(i)}
                  className="aspect-square overflow-hidden bg-atelier-surface proj-img-shell group relative atelier-border-soft hover:border-atelier-ink/60 transition-all w-full"
                  data-testid={`gallery-item-${g.id}`}
                >
                  {isVid ? (
                    <>
                      <video src={g.image_url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                      <div className="absolute inset-0 flex items-center justify-center bg-atelier-deep/40 group-hover:bg-atelier-deep/20 transition">
                        <div className="w-12 h-12 bg-atelier-ink/90 flex items-center justify-center">
                          <svg viewBox="0 0 24 24" className="w-5 h-5 ml-0.5 text-atelier-deep" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                        </div>
                      </div>
                    </>
                  ) : (
                    <img src={g.image_url} alt={g.title} className="w-full h-full object-cover" />
                  )}
                  {g.title && (
                    <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-atelier-deep/90 to-transparent text-atelier-text text-xs opacity-0 group-hover:opacity-100 transition font-serif">
                      {g.title}
                    </div>
                  )}
                </button>
              </Reveal>
            );
          })}
        </div>
      </section>

      {lightboxIndex !== null && (
        <MediaLightbox
          items={mediaItems}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onChange={setLightboxIndex}
        />
      )}
    </div>
  );
}
